import { Button, ButtonVariant, type ButtonProps } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import type { ReactNode } from "react";
import { Link, useRouter } from "wouter";
import type { PaletteCommand } from "./types";
import { usePaletteCommands } from "./use-palette-commands";

type CommandButtonProps = Omit<ButtonProps, "asChild" | "children" | "onClick"> & {
  commandId?: string;
  label: string;
  icon?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
} & ({ onClick: () => void; to?: never } | { to: string; onClick?: never });

// Mirrors wouter's `Link`, because the palette navigates from the root router.
function resolveLink(to: string, base: string): string {
  return to.startsWith("~") ? to.slice(1) : `${base}${to}`;
}

export function CommandButton({
  commandId,
  label,
  icon,
  loading = false,
  onClick,
  to,
  children,
  ...props
}: CommandButtonProps) {
  const router = useRouter();
  const disabled = props.disabled || loading;
  // the wheel would be invisible in the primary color on filled backgrounds
  const inheritLoaderColor = !props.variant || props.variant === ButtonVariant.Filled;
  const command: PaletteCommand | undefined =
    commandId && !disabled
      ? {
          id: commandId,
          title: label,
          icon,
          ...(to ? { to: resolveLink(to, router.base) } : { perform: () => onClick?.() }),
        }
      : undefined;
  usePaletteCommands(command ? [command] : []);

  const content = children ?? (
    <>
      {loading ? (
        <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor={inheritLoaderColor} aria-hidden />
      ) : (
        icon
      )}
      {label}
    </>
  );

  if (to) {
    return (
      <Button asChild {...props}>
        <Link to={to}>{content}</Link>
      </Button>
    );
  }

  return (
    <Button type="button" {...props} disabled={disabled} aria-busy={loading} onClick={() => onClick?.()}>
      {content}
    </Button>
  );
}
